import React, { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Plane, MapPin, Compass, ArrowRight, MessageCircle, Sparkles } from "lucide-react";
import { HERO_SLIDES, BRAND } from "../data/site";

export default function Hero() {
  const [idx, setIdx] = useState(0);

  useEffect(() => {
    const id = setInterval(
      () => setIdx((p) => (p + 1) % HERO_SLIDES.length),
      5500
    );
    return () => clearInterval(id);
  }, []);

  const handlePlan = () => {
    const el = document.getElementById("contact");
    if (el) {
      const top = el.getBoundingClientRect().top + window.scrollY - 80;
      window.scrollTo({ top, behavior: "smooth" });
    }
  };

  const handleExplore = () => {
    const el = document.getElementById("destinations");
    if (el) {
      const top = el.getBoundingClientRect().top + window.scrollY - 80;
      window.scrollTo({ top, behavior: "smooth" });
    }
  };

  return (
    <section
      id="home"
      data-testid="hero-section"
      className="relative min-h-[100svh] w-full overflow-hidden pt-24 pb-16"
    >
      {/* Slideshow */}
      <div className="absolute inset-0 z-0">
        <AnimatePresence mode="sync">
          <motion.div
            key={idx}
            initial={{ opacity: 0, scale: 1.08 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0, scale: 1.04 }}
            transition={{ duration: 1.4, ease: [0.22, 1, 0.36, 1] }}
            className="absolute inset-0"
          >
            <img
              src={HERO_SLIDES[idx].image}
              alt={HERO_SLIDES[idx].place}
              className="w-full h-full object-cover"
            />
          </motion.div>
        </AnimatePresence>
        {/* Layered overlays */}
        <div className="absolute inset-0 bg-gradient-to-b from-slate-900/50 via-slate-900/25 to-slate-900/70" />
        <div className="absolute inset-0 bg-gradient-to-tr from-brand-ocean/30 via-transparent to-brand-orange/20" />
      </div>

      {/* Floating decorative elements */}
      <motion.div
        animate={{ y: [0, -16, 0], rotate: [0, 4, 0] }}
        transition={{ duration: 7, repeat: Infinity, ease: "easeInOut" }}
        className="hidden md:flex absolute top-32 right-10 lg:right-24 z-10 h-20 w-20 lg:h-24 lg:w-24 items-center justify-center rounded-full glass shadow-2xl"
      >
        <Plane className="w-10 h-10 text-brand-orange -rotate-45" />
      </motion.div>
      <motion.div
        animate={{ y: [0, 14, 0] }}
        transition={{ duration: 6, repeat: Infinity, ease: "easeInOut", delay: 0.6 }}
        className="hidden lg:flex absolute bottom-36 left-10 z-10 h-16 w-16 items-center justify-center rounded-full glass shadow-xl"
      >
        <Compass className="w-7 h-7 text-brand-ocean" />
      </motion.div>
      <motion.div
        animate={{ y: [0, -10, 0], x: [0, 8, 0] }}
        transition={{ duration: 8, repeat: Infinity, ease: "easeInOut", delay: 1 }}
        className="hidden md:flex absolute top-1/2 left-6 lg:left-20 z-10 h-14 w-14 items-center justify-center rounded-2xl glass-dark text-white shadow-xl"
      >
        <Sparkles className="w-6 h-6 text-amber-300" />
      </motion.div>

      {/* Content */}
      <div className="relative z-20 mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 flex flex-col justify-center min-h-[calc(100svh-7rem)]">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 1, ease: [0.22, 1, 0.36, 1] }}
          className="max-w-3xl"
        >
          {/* Eyebrow pill */}
          <div
            data-testid="hero-eyebrow"
            className="inline-flex items-center gap-2 px-4 py-2 rounded-full glass-dark text-white/95 text-xs tracking-[0.22em] uppercase shadow-lg"
          >
            <MapPin className="w-3.5 h-3.5 text-brand-orange" />
            Trusted travel partner since {BRAND.established}
          </div>

          {/* Headline — natural wrapping, no forced breaks */}
          <h1
            data-testid="hero-headline"
            className="mt-5 font-playfair text-white text-5xl sm:text-6xl lg:text-7xl font-bold leading-[1.04] tracking-tight drop-shadow-[0_2px_30px_rgba(0,0,0,0.45)] text-balance"
          >
            Crafting{" "}
            <span className="italic text-brand-orange">unforgettable</span>{" "}
            memories, one journey at a time.
          </h1>

          {/* Short sub-copy */}
          <p
            data-testid="hero-subheadline"
            className="mt-5 text-lg sm:text-xl text-white/90 max-w-xl leading-relaxed drop-shadow-md"
          >
            From Kashmir's snow-peaks to Bali's sunsets — Happy Journey 4U
            handcrafts personal holidays from the heart of Burhanpur.
          </p>

          {/* CTA pair */}
          <div className="mt-8 flex flex-wrap gap-3">
            <button
              data-testid="hero-plan-trip-btn"
              onClick={handlePlan}
              className="group inline-flex items-center gap-2 px-7 py-4 rounded-full bg-brand-orange text-white font-semibold shadow-[0_14px_40px_rgba(243,146,0,0.45)] hover:bg-brand-orangeDark hover:shadow-[0_18px_50px_rgba(243,146,0,0.6)] transition-all focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-white"
            >
              Plan Your Trip
              <ArrowRight className="w-5 h-5 transition-transform group-hover:translate-x-1" />
            </button>
            <a
              href={`https://wa.me/${BRAND.phones[0].raw}?text=${encodeURIComponent("Hi Happy Journey 4U! I'd like to plan a trip.")}`}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="hero-whatsapp-btn"
              className="inline-flex items-center gap-2 px-7 py-4 rounded-full bg-[#25D366] text-white font-semibold shadow-[0_10px_30px_rgba(37,211,102,0.4)] hover:bg-[#1eb858] hover:shadow-[0_14px_40px_rgba(37,211,102,0.5)] transition-all focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-white"
            >
              <MessageCircle className="w-5 h-5" />
              WhatsApp Us
            </a>
          </div>

          {/* Explore destinations link */}
          <div className="mt-5">
            <button
              data-testid="hero-explore-btn"
              onClick={handleExplore}
              className="text-white/80 text-sm hover:text-white underline underline-offset-4 decoration-white/40 hover:decoration-white transition-all focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-white focus-visible:ring-offset-1 rounded"
            >
              Explore popular destinations ↓
            </button>
          </div>

          {/* Slide indicator — "Now Exploring" widget, visually separated */}
          <div className="mt-12 flex items-center gap-3" data-testid="hero-indicators">
            {HERO_SLIDES.map((s, i) => (
              <button
                key={s.place}
                onClick={() => setIdx(i)}
                aria-label={`Show ${s.place}`}
                data-testid={`hero-indicator-${i}`}
                className="group flex items-center gap-2 focus-visible:outline-none"
              >
                <span
                  className={`block h-[3px] rounded-full transition-all duration-500 ${
                    i === idx ? "w-10 bg-brand-orange" : "w-5 bg-white/40 hover:bg-white/70"
                  }`}
                />
              </button>
            ))}
            {/* Now exploring chip — clearly separated from heading block */}
            <div className="ml-3 hidden sm:flex items-center gap-3 pl-3 border-l border-white/25">
              <div className="flex flex-col leading-tight">
                <span className="text-[0.65rem] tracking-[0.22em] uppercase text-white/60">
                  Now exploring
                </span>
                <span className="font-playfair text-white text-base italic">
                  {HERO_SLIDES[idx].place} — {HERO_SLIDES[idx].tagline}
                </span>
              </div>
            </div>
          </div>
        </motion.div>

        {/* Stats glass strip */}
        <div
          data-testid="hero-stats"
          className="hidden md:grid grid-cols-4 gap-0 mt-14 max-w-2xl glass rounded-2xl overflow-hidden"
        >
          {[
            { k: "8+", v: "Years" },
            { k: "10K+", v: "Travellers" },
            { k: "50+", v: "Destinations" },
            { k: "4.9★", v: "Avg. Rating" },
          ].map((s, i) => (
            <div
              key={s.v}
              className={`text-center py-4 px-3 ${
                i < 3 ? "border-r border-white/30" : ""
              }`}
            >
              <div className="font-playfair text-2xl font-bold text-slate-900">
                {s.k}
              </div>
              <div className="text-[0.7rem] uppercase tracking-[0.18em] text-slate-600 mt-0.5">
                {s.v}
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}
